import { AlertCircle, Loader2, Mail } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactElement, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/routing';

import { ClaimIdentityGateModal } from './ClaimIdentityGateModal';

interface ClaimMethodsBlockProps {
  /** Non-gated `ProfessionalRead.has_email` — true if the DB row has an
      address (the email field itself is tier-gated to null for free tiers). */
  hasEmail: boolean;
  professionalName: string;
  isLoading: boolean;
  error: string | null;
  onVerifyViaEmail: () => void;
  /** Self-declaration gate (#8306): visitor confirmed this ISN'T their
      profile — routes to the no-email third-party notice instead. */
  onDeclineSelf: () => void;
}

/**
 * Primary claim action + supporting context.
 *
 * SOTA hierarchy:
 *   1. ONE dominant filled button (verify-via-email) — variant='brand' (solid brand fill).
 *   2. Expiry / timing reassurance directly underneath the CTA.
 *   3. Terms line at the bottom — required but visually quiet.
 */
export function ClaimMethodsBlock({
  hasEmail,
  professionalName,
  isLoading,
  error,
  onVerifyViaEmail,
  onDeclineSelf,
}: ClaimMethodsBlockProps): ReactElement {
  const t = useTranslations('Lawyers.claim');
  const [gateOpen, setGateOpen] = useState(false);

  function handleConfirmSelf(): void {
    setGateOpen(false);
    onVerifyViaEmail();
  }

  function handleDeclineSelf(): void {
    setGateOpen(false);
    onDeclineSelf();
  }

  return (
    <div className='space-y-4'>
      {error ? (
        <Alert
          variant='destructive'
          className='border-status-error/30 bg-status-error/5'
        >
          <AlertCircle className='h-4 w-4 text-status-error' />
          <AlertDescription className='text-status-error'>
            {error}
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Primary CTA — use variant='brand' for functional in-app buttons;
          brandGradient is reserved for marketing CTAs only. No shimmer: the
          animated sweep drew the eye but read as a loading state and added
          visual noise; a solid brand button + the destination email below is
          calmer and clearer.

          Opens the identity gate (#8306) instead of firing the claim
          directly — the verification email must never send until the
          visitor confirms they ARE the professional. */}
      <Button
        variant='brand'
        className='h-12 w-full text-base shadow-lg shadow-brand-tertiary/20 hover:shadow-xl hover:shadow-brand-tertiary/30'
        onClick={() => setGateOpen(true)}
        disabled={isLoading || !hasEmail}
      >
        {isLoading ? (
          <Loader2 className='mr-2 h-5 w-5 animate-spin' />
        ) : (
          <Mail className='mr-2 h-5 w-5' />
        )}
        {t('verifyViaEmail')}
      </Button>

      <ClaimIdentityGateModal
        open={gateOpen}
        onOpenChange={setGateOpen}
        professionalName={professionalName}
        isSubmitting={isLoading}
        onConfirmSelf={handleConfirmSelf}
        onDeclineSelf={handleDeclineSelf}
      />

      {/* Expiry copy lives on the success card (ClaimEmailSentCard), where
          it describes the link the user just received. Pre-click, showing a
          countdown for something that hasn't happened yet is noise. */}

      {!hasEmail ? (
        <p className='text-sm text-content-muted text-center'>
          {t('noEmailOnRecord')}
        </p>
      ) : null}

      {/* Terms — required legal line, kept quiet */}
      <p className='text-xs text-content-muted text-center pt-1'>
        {t('termsNotice')}{' '}
        <Link
          href='/terms-of-use'
          className='text-brand-tertiary hover:text-brand-tertiary-hover underline underline-offset-2'
        >
          {t('lawyerTerms')}
        </Link>
      </p>
    </div>
  );
}
